'';

import { useState, useEffect } from 'react';

import { useTranslate } from 'src/locales'; // your hook

export const usePlacePredictions = (
  inputValue,
  { debounceMs = 300, country = [], language = 'ar', ready = true } = {}
) => {
  const [debouncedValue, setDebouncedValue] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isError, setError] = useState(false);
  const { currentLang } = useTranslate();
  const [isPaste, setIsPaste] = useState(false);

  // Function to call on paste event
  const handlePaste = () => setIsPaste(true);

  // Debounce input
  useEffect(() => {
    if (!inputValue?.trim()) {
      setDebouncedValue('');
      setPredictions([]);
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedValue(inputValue?.trim());
    }, debounceMs);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(handler);
  }, [inputValue, debounceMs]);

  // Fetch predictions
  useEffect(() => {
    if (!window.google?.maps?.places || !debouncedValue || !ready) return;

    setLoading(true);
    setError(false);

    if (isPaste && debouncedValue?.length > 15) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.findPlaceFromQuery(
        {
          query: debouncedValue,
          fields: ['geometry', 'formatted_address', 'place_id', 'name'],
        },
        (results, status) => {
          setLoading(false);
          setIsPaste(false); // reset after handling
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.length) {
            setPredictions(
              results.map((r) => ({
                description: r.formatted_address,
                place_id: r.place_id,
                geometry: r.geometry,
              }))
            );
          } else {
            setError(true);
            setPredictions([]);
          }
        }
      );
      return;
    } else {
      // 🔹 Use autocomplete when typing
      const autocompleteService = new window.google.maps.places.AutocompleteService();
      autocompleteService.getPlacePredictions(
        {
          input: debouncedValue,
          componentRestrictions: { country },
          language: currentLang.value,
        },
        (results, status) => {
          setLoading(false);
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setPredictions(results || []);
          } else {
            setError(true);
            setPredictions([]);
          }
        }
      );
    }
  }, [debouncedValue, country, language, ready, currentLang.value]);

  return { predictions, isLoading, isError, handlePaste };
};

export function useCurrentLocation(options = {}) {
  const [location, setLocation] = useState(null); // { lat, lng }
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError(new Error('Geolocation is not supported by your browser.'));
      setLoading(false);
      return;
    }
    // getLocation();
  }, []);

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setLoading(false);
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }); // ✅ resolves with { lat, lng }
        },
        (err) => {
          setError(err);
          setLoading(false);
          reject(err); // ✅ reject error for await/catch
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          ...options,
        }
      );
    });
  };

  return {
    location,
    error,
    loading,
    getLocation,
  };
}

// eslint-disable-next-line arrow-body-style
export const getAddressFromPlaceId = async (placeId) => {
  return new Promise((resolve, reject) => {
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));

    service.getDetails(
      {
        placeId,
        fields: ['geometry', 'formatted_address', 'place_id', 'name', 'address_components'],
      },
      (results, status) => {
        if (status === 'OK' && results) {
          resolve([
            {
              description: results.formatted_address,
              place_id: results.place_id,
              geometry: results.geometry,
            },
          ]);
        } else {
          reject(new Error(`Place Details failed: ${status}`));
        }
      }
    );
  });
};
